import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Upload, Trash2, X, FileUp, Landmark, Edit3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n, cur = "TRY") =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(n || 0);

function Modal({ title, children, onClose, wide }) {
  return <div className="modal-backdrop" onClick={onClose} data-testid="modal-backdrop">
    <div className={`modal ${wide ? "modal-wide" : ""}`} onClick={e => e.stopPropagation()}>
      <div className="modal-head"><h3>{title}</h3><button onClick={onClose} data-testid="modal-close"><X size={18} /></button></div>
      {children}
    </div>
  </div>;
}
function Field({ label, children }) { return <label className="field"><span>{label}</span>{children}</label>; }

export default function BankAccountsPage({ company, companies, user }) {
  const canEdit = user?.role !== "viewer";
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showStatements, setShowStatements] = useState(null);
  const [importMsg, setImportMsg] = useState("");

  const load = async () => {
    const { data } = await axios.get(`${API}/bank-accounts`, { params: { company_id: company } });
    setAccounts(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [company]);

  const uploadStatement = async (bid, ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const form = new FormData(); form.append("file", file);
    setImportMsg("Ekstre yükleniyor...");
    try {
      const { data } = await axios.post(`${API}/bank-accounts/${bid}/statement`, form);
      setImportMsg(`${data.imported} hareket aktarıldı · Net ${money(data.net_change)} · Yeni bakiye ${money(data.new_balance)}`);
      load();
    } catch (e) { setImportMsg(e.response?.data?.detail || "Hata"); }
    ev.target.value = "";
  };

  const removeAcc = async (a) => {
    if (!window.confirm(`${a.name} silinsin mi?`)) return;
    await axios.delete(`${API}/bank-accounts/${a.id}`); load();
  };

  const totalTry = accounts.reduce((s, a) => s + (a.balance_try || 0), 0);

  return <div className="page">
    <section className="page-toolbar">
      <div>
        <p className="eyebrow">BANKA HESAPLARI</p>
        <h2>Toplam Bakiye: {money(totalTry)}</h2>
        <p className="muted">{accounts.length} hesap · Banka ekstresi CSV/Excel ile toplu bakiye güncellemesi.</p>
      </div>
      {canEdit && <div className="toolbar-actions">
        <button className="primary-btn" data-testid="bank-new-btn" onClick={() => setShowForm(true)}><Plus size={17} /> Yeni hesap</button>
      </div>}
    </section>

    {importMsg && <div className="panel" style={{ padding: 12, marginBottom: 12, background: "#eff6ff", color: "#1e40af" }} data-testid="bank-import-msg">{importMsg}</div>}

    <section className="metric-grid" data-testid="bank-list">
      {accounts.map(a => (
        <div key={a.id} className="metric-card" data-testid={`bank-card-${a.id}`}>
          <div className="metric-head">
            <span className="metric-label"><Landmark size={13} /> {a.bank}</span>
            <span className="company-pill">{(a.company_id || "").toUpperCase()}</span>
          </div>
          <div className="metric-value">{money(a.balance, a.currency)}</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            {a.name} {a.currency !== "TRY" && `· TL eş. ${money(a.balance_try)}`}
          </div>
          {a.iban && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6, fontFamily: "monospace" }}>{a.iban}</div>}
          {canEdit && <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
            <label className="outline-btn" style={{ padding: "4px 10px", fontSize: 12 }} data-testid={`bank-upload-${a.id}`}>
              <FileUp size={13} /> Ekstre
              <input type="file" accept=".csv,.xlsx,.xls" hidden onChange={(e) => uploadStatement(a.id, e)} />
            </label>
            <button className="outline-btn" style={{ padding: "4px 10px", fontSize: 12 }} data-testid={`bank-statements-${a.id}`} onClick={() => setShowStatements(a)}>Hareketler</button>
            <button className="outline-btn" style={{ padding: "4px 10px", fontSize: 12 }} data-testid={`bank-edit-${a.id}`} onClick={() => setEditing(a)}><Edit3 size={12} /></button>
            {user?.role === "admin" && <button className="icon-btn" style={{ padding: 4 }} data-testid={`bank-del-${a.id}`} onClick={() => removeAcc(a)}><Trash2 size={13} /></button>}
          </div>}
        </div>
      ))}
      {accounts.length === 0 && <div className="muted" style={{ padding: 24 }}>Henüz banka hesabı yok.</div>}
    </section>

    {showForm && <BankForm companies={companies} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />}
    {editing && <BankForm companies={companies} initial={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    {showStatements && <StatementsModal account={showStatements} onClose={() => setShowStatements(null)} />}
  </div>;
}

function BankForm({ companies, initial, onClose, onDone }) {
  const [f, setF] = useState(initial || { name: "", bank: "", iban: "", currency: "TRY", balance: 0, company_id: companies?.[0]?.id || "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const p = { ...f, balance: parseFloat(f.balance || 0) };
      if (initial) await axios.patch(`${API}/bank-accounts/${initial.id}`, p);
      else await axios.post(`${API}/bank-accounts`, p);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || "Hata"); }
    finally { setBusy(false); }
  };
  return <Modal onClose={onClose} title={initial ? "Hesabı düzenle" : "Yeni banka hesabı"}>
    <form onSubmit={submit} className="form">
      <div className="row2">
        <Field label="Banka"><input data-testid="bank-form-bank" value={f.bank} onChange={e => setF({ ...f, bank: e.target.value })} required placeholder="İş Bankası" /></Field>
        <Field label="Hesap adı"><input data-testid="bank-form-name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} required placeholder="Merkez TL" /></Field>
      </div>
      <Field label="IBAN"><input data-testid="bank-form-iban" value={f.iban} onChange={e => setF({ ...f, iban: e.target.value })} placeholder="TR00..." /></Field>
      <div className="row2">
        <Field label="Para birimi"><select data-testid="bank-form-currency" value={f.currency} onChange={e => setF({ ...f, currency: e.target.value })}>
          {["TRY", "USD", "EUR", "GBP"].map(c => <option key={c}>{c}</option>)}
        </select></Field>
        <Field label="Açılış bakiye"><input data-testid="bank-form-balance" type="number" step="0.01" value={f.balance} onChange={e => setF({ ...f, balance: e.target.value })} /></Field>
      </div>
      <Field label="Şirket"><select data-testid="bank-form-company" value={f.company_id} onChange={e => setF({ ...f, company_id: e.target.value })}>
        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select></Field>
      {err && <div className="auth-err">{err}</div>}
      <div className="form-actions">
        <button type="button" className="outline-btn" onClick={onClose}>Vazgeç</button>
        <button data-testid="bank-form-submit" className="primary-btn" disabled={busy}>{busy ? "..." : "Kaydet"}</button>
      </div>
    </form>
  </Modal>;
}

function StatementsModal({ account, onClose }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    axios.get(`${API}/bank-accounts/${account.id}/statements`, { params: { limit: 200 } }).then(r => setRows(r.data));
  }, [account.id]);
  return <Modal onClose={onClose} wide title={`${account.name} — Son hareketler`}>
    <div className="muted" style={{ padding: "8px 0 14px" }}>{rows.length} kayıt</div>
    <div className="table-scroll"><table>
      <thead><tr><th>TARİH</th><th>AÇIKLAMA</th><th>BORÇ</th><th>ALACAK</th><th>BAKİYE</th></tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan="5" style={{ textAlign: "center", padding: 16 }} className="muted">Henüz hareket yok. CSV/Excel ekstre yükleyin.</td></tr>}
        {rows.map(r => <tr key={r.id} data-testid={`stmt-row-${r.id}`}>
          <td>{r.txn_date}</td>
          <td>{r.description}</td>
          <td className="expense">{r.debit ? money(r.debit, account.currency) : "—"}</td>
          <td className="income">{r.credit ? money(r.credit, account.currency) : "—"}</td>
          <td>{r.balance_after != null ? money(r.balance_after, account.currency) : "—"}</td>
        </tr>)}
      </tbody>
    </table></div>
  </Modal>;
}
